<!-- frontend/src/components/dialogs/StudentArriveConfirmDialog.vue -->
<template>
  <div
    v-if="open"
    class="overlay-dialog"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="overlay-backdrop"
      aria-hidden="true"
      @click="$emit('close')"
    />
    <div
      class="dialog-card"
      role="document"
    >
      <header class="dialog-header">
        <h3 class="h2">
          Ankunft bestätigen
        </h3>
      </header>

      <div class="dialog-body">
        <div class="form-grid">
          <label class="field w-40">
            <span class="label">Datum</span>
            <flat-pickr
              v-model="dateValue"
              :config="dateOpts"
              class="input"
              aria-label="Datum wählen"
              @on-change="onDateChangeFp"
            />
          </label>

          <label class="field w-30">
            <span class="label">Uhrzeit</span>
            <flat-pickr
              v-model="timeString"
              :config="timeOpts"
              class="input"
              aria-label="Uhrzeit wählen"
              @on-change="onTimeChangeFp"
            />
          </label>
        </div>

        <p class="mb-0 mt-12">
          Soll
          <strong>{{ studentName || 'Schüler' }}</strong>
          am
          <strong>{{ dateDisplay }}</strong>
          um
          <strong>{{ timeString }}</strong>
          Uhr als angekommen erfasst werden?
        </p>
      </div>

      <footer class="dialog-actions">
        <button
          class="btn"
          type="button"
          @click="$emit('close')"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-primary"
          type="button"
          @click="onConfirm"
        >
          <i
            class="pi pi-sign-in"
            aria-hidden="true"
          />
          <span style="margin-left:6px;">Ankunft speichern</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {ref, watch, computed} from 'vue';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

const props = defineProps({
  open: {type: Boolean, default: false},
  studentName: {type: String, default: ''},
  // Optional: vorkonfiguriertes Datum/Zeit (z. B. aus Timeline)
  occurredAt: {type: String, default: ''},
  defaultDate: {type: [String, Date, null], default: null}
});

const emit = defineEmits(['close', 'confirm']);

const dateValue = ref(null); // Date-Objekt für den Datepicker
const timeString = ref(nowHHMM()); // 'HH:mm'

const dateOpts = {
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  locale: German,
  allowInput: true
};

const timeOpts = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  locale: German,
  allowInput: true
};

function nowHHMM() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function asDateOnly(val) {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    // Datum: Timeline (defaultDate) → ov-date → occurredAt → heute
    const ovd = getOvDateAsDate();
    const base =
      (props.defaultDate ? asDateOnly(props.defaultDate) : null) ??
      (ovd ? asDateOnly(ovd) : null) ??
      (props.occurredAt ? asDateOnly(props.occurredAt) : null) ??
      asDateOnly(new Date());

    dateValue.value = base;
    // Uhrzeit immer auf now()
    timeString.value = nowHHMM();
  }
);

function onDateChangeFp(selectedDates) {
  // v-model hält Date; keine Sonderlogik nötig
  dateValue.value = selectedDates?.[0] ?? dateValue.value;
}

function onTimeChangeFp(selectedDates, dateStr) {
  timeString.value = (dateStr || '').slice(0, 5);
}

const dateDisplay = computed(() => {
  const d = dateValue.value instanceof Date ? dateValue.value : null;
  if (!d) return '';
  return d.toLocaleDateString('de-DE');
});

function getOvDateAsDate() {
  try {
    const el = document.getElementById('ov-date');
    if (!el) return null;
    const anyEl = el;
    if (anyEl._flatpickr && Array.isArray(anyEl._flatpickr.selectedDates) && anyEl._flatpickr.selectedDates[0]) {
      return anyEl._flatpickr.selectedDates[0];
    }
    const raw = (el.value || '').toString().trim();
    if (!raw) return null;

    // 1) ISO / Date.parse-kompatibel
    const iso = new Date(raw);
    if (!Number.isNaN(iso.getTime())) return iso;

    // 2) dd.mm.yyyy
    const m = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  } catch {
    return null;
  }
}

function normalizeDateValue(val) {
  if (val instanceof Date) {
    return new Date(val);
  }
  if (typeof val === 'string' && val.trim()) {
    // 1) ISO / Date.parse-kompatibel
    const iso = new Date(val);
    if (!Number.isNaN(iso.getTime())) {
      return iso;
    }
    // 2) dd.mm.yyyy
    const m = val.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    }
  }
  // Fallback: jetzt
  return new Date();
}

function buildOccurredAt() {
  const base = normalizeDateValue(dateValue.value);
  const m = (timeString.value || '').match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (m) {
    const [, hh, mm] = m;
    base.setHours(Number(hh), Number(mm), 0, 0);
  }
  return base.toISOString();
}

function onConfirm() {
  const iso = buildOccurredAt();
  emit('confirm', { occurredAt: iso });
}
</script>

<style scoped>
.overlay-dialog {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
}

.overlay-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .35);
}

.dialog-card {
  position: relative;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, .2);
  width: 92%;
  max-width: 420px;
}

.dialog-header {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.dialog-body {
  padding: 14px;
}

.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

.dialog-actions {
  padding: 12px 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--border);
}

/* kleines Formular-Layout im Dialog */
.form-grid {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 500;
}

.input {
  height: 34px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  font-size: 14px;
}

.input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.mt-12 { margin-top: 12px; }
.mb-0 { margin-bottom: 0; }
</style>
